import React, { useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { handleModalAction } from "../../../../utils/helpers/handleModalAction";
import useReminders from "../hooks/useReminders";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";

const EditReminderModal = ({ data, module }) => {
  const { editReminder } = useReminders(module);

  // Use ref to track initial values
  const initialValuesRef = useRef({
    reminderId: "",
    reminderDateTime: new Date(),
    reminderMessage: "",
    leadMetaDetails: {
      leadName: "",
      status: "",
      phoneno: "",
    },
  });

  // Formik Setup
  const formik = useFormik({
    initialValues: initialValuesRef.current,
    validationSchema: Yup.object({
      reminderDateTime: Yup.date().required("Reminder date is required"),
      reminderMessage: Yup.string().required("Reminder message is required"),
    }),
    onSubmit: (values) => {
      if (JSON.stringify(initialValuesRef.current) === JSON.stringify(values)) {
        toast.warning("No changes made to update.");
        return;
      }

      const payload = {
        ...values,
        reminderDateTime: new Date(values.reminderDateTime).toISOString(), // Convert to UTC
      };

      editReminder.mutate(
        { reminderId: values.reminderId, values: payload },
        {
          onSuccess: () => {
            formik.resetForm();
            handleModalAction("editReminderModal", "hide");
          },
        }
      );
    },
  });

  // Update initialValuesRef and formik values when data changes
  useEffect(() => {
    if (data) {
      const updatedValues = {
        reminderId: data?._id || "",
        reminderDateTime: data?.reminderDateTime
          ? new Date(data.reminderDateTime)
          : new Date(),
        reminderMessage: data?.reminderMessage || "",
        leadMetaDetails: {
          leadName: data?.leadMetaDetails?.leadName || "",
          status: data?.leadMetaDetails?.status || "",
          phoneno: data?.leadMetaDetails?.phoneno || "",
        },
      };

      initialValuesRef.current = updatedValues;
      formik.setValues(updatedValues);
    }
  }, [data]);

  // Handle separate date and time updates
  const handleDateChange = (date) => {
    const currentDate = new Date(formik.values.reminderDateTime);
    const updatedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      currentDate.getHours(),
      currentDate.getMinutes()
    );
    formik.setFieldValue("reminderDateTime", updatedDate);
  };

  const handleTimeChange = (time) => {
    const currentDate = new Date(formik.values.reminderDateTime);
    const updatedDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
      time.getHours(),
      time.getMinutes()
    );
    formik.setFieldValue("reminderDateTime", updatedDate);
  };

  return ReactDOM.createPortal(
    <div
      className="modal fade"
      id="editReminderModal"
      tabIndex="-1"
      aria-labelledby="editReminderModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog">
        <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-header">
            <h5 className="modal-title" id="editReminderModalLabel">
              Edit Reminder for {data?.leadMetaDetails?.leadName}
            </h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          {/* Modal Body */}
          <div className="modal-body">
            <form onSubmit={formik.handleSubmit}>
              {/* Date & Time Picker */}
              <div className="mb-3">
                <label className="form-label">Select Date & Time</label>
                <div className="d-flex gap-2">
                  {/* Date Picker */}
                  <DatePicker
                    selected={formik.values.reminderDateTime}
                    onChange={handleDateChange}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                    highlightDates={[new Date()]}
                  />

                  {/* Time Picker */}
                  <DatePicker
                    selected={formik.values.reminderDateTime}
                    onChange={handleTimeChange}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={5}
                    dateFormat="h:mm aa"
                    className="form-control"
                  />
                </div>
                {formik.errors.reminderDateTime && (
                  <div className="text-danger">
                    {formik.errors.reminderDateTime}
                  </div>
                )}
              </div>

              {/* Reminder Message */}
              <div className="mb-3">
                <label className="form-label">Reminder Message</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Enter reminder details..."
                  {...formik.getFieldProps("reminderMessage")}
                ></textarea>
                {formik.errors.reminderMessage && (
                  <div className="text-danger">
                    {formik.errors.reminderMessage}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={editReminder.isPending}
                >
                  {editReminder.isPending ? "Updating..." : "Update Reminder"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditReminderModal;
